import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'

// Errors that are browser noise rather than app bugs.
const NOISE_PATTERNS = [
  /ResizeObserver loop/i,
  /Non-Error promise rejection/i,
]

function isNoise(event: Sentry.ErrorEvent, hint?: Sentry.EventHint): boolean {
  const original = hint?.originalException
  const texts = [
    event.message,
    ...(event.exception?.values ?? []).map((v) => v.value),
    typeof original === 'string' ? original : original instanceof Error ? original.message : undefined,
  ]
  return texts.some((t) => !!t && NOISE_PATTERNS.some((re) => re.test(t)))
}

function stripQuery(url: string | undefined): string | undefined {
  // Query strings can carry tokens or user input; keep only the path.
  return url ? url.split(/[?#]/)[0] : url
}

/** Exported for tests. Removes anything that could carry user data. */
export function scrubEvent(event: Sentry.ErrorEvent, hint?: Sentry.EventHint): Sentry.ErrorEvent | null {
  if (isNoise(event, hint)) return null

  if (event.request) {
    delete event.request.cookies
    delete event.request.headers
    delete event.request.data
    delete event.request.query_string
    event.request.url = stripQuery(event.request.url)
  }
  // Breadcrumbs (console, clicks, fetch URLs) can include user-entered text.
  delete event.breadcrumbs
  delete event.user
  return event
}

/**
 * Reports uncaught browser errors to GlitchTip.
 * Complete no-op without VITE_GLITCHTIP_DSN (dev, CI).
 *
 * @sentry/vue 11 has no `sendDefaultPii` switch (it attaches no PII by
 * default); scrubEvent removes the rest. `_router` is reserved for router
 * instrumentation, which stays off while tracing is off.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for router instrumentation; tracing is off
export function initErrorReporting(app: App, _router?: Router): void {
  const dsn = import.meta.env.VITE_GLITCHTIP_DSN
  if (!dsn) return

  Sentry.init({
    app,
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_RELEASE || undefined,
    tracesSampleRate: 0,
    attachStacktrace: true,
    beforeSend: scrubEvent,
  })
}
